import React from 'react';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { Tag, TextLink, Typography } from '@guildeducationinc/recess';
import FundingPictogram from '@guildeducationinc/recess/pictograms/FundingPictogram';
import { BENEFITS_URL } from '@src/utilities/envConfig';
import { GetMemberProgramQuery } from '@src/graphqlTypes/catalogServiceTypes';
import { ShowMore } from '@src/components/ShowMore';
import { formatMoney } from '@src/utilities/numberFormattingUtility';
import { getCostMapping, getRemainingCosts } from '@src/constants/cost';
import { usePricebook } from '@src/components/Providers/PricebookProvider';
import { Skeleton } from '@src/components/UserFacetSkeletonLoader';
import { CostPopover } from '../CostPopover';
import styles from './Tuition.module.scss';

interface TuitionProps {
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
}

export const Tuition: React.FC<TuitionProps> = ({ loading, program }) => {
	const { userFromIdToken: user } = useMemberExperienceAuth();
	const { programCost, programCostLoading, programCostUnavailable } = usePricebook();
	const employerName = user.employer;
	const remainingCosts = getRemainingCosts(program.fundingLevel, programCost);
	const tuition = formatMoney(programCost);
	const funding = formatMoney(program.fundingLevel);
	const cost = formatMoney(remainingCosts);
	const costMapping = getCostMapping(funding);
	const isFullyCovered = remainingCosts === 0;

	return (
		<section
			aria-label='program tuition'
			className={styles.sectionContainer}
			data-testid='program-detail-tuition'
			data-title='Program tuition'
			id='detail-tuition'
			tabIndex={-1}
		>
			<div className={styles.header}>
				<Typography variant='h2'>Program tuition</Typography>
				<Tag appearance='quiet' color='grey'>
					New
				</Tag>
			</div>
			<Typography variant='body2'>
				Learn more about{' '}
				<TextLink href={BENEFITS_URL} size='large'>
					what&apos;s covered
				</TextLink>{' '}
				with your benefit.
			</Typography>

			<Skeleton dataLoading={loading || programCostLoading} height={156}>
				<ShowMore section='tuition'>
					<>
						{isFullyCovered && (
							<div className={styles.fullyCovered}>
								<FundingPictogram className={styles.fullyCoveredPictogram} size={48} />
								<Typography variant='title2' weight='300'>
									Funding should cover the cost of your program.
								</Typography>
							</div>
						)}
						<Typography className={styles.costItem} variant='body2'>
							{programCostUnavailable ? (
								"Estimated tuition isn't available."
							) : (
								<>
									Estimated <Typography variant='title3'>tuition</Typography> is{' '}
									<Typography variant='title3'>{tuition}</Typography>
								</>
							)}
						</Typography>
						<Typography variant='body1'>
							{costMapping.tuition.subtext}
							<CostPopover
								eventText={costMapping.tuition.subtext}
								id='tuition'
								location='section'
							/>
						</Typography>

						<Typography className={styles.costItem} testid='tuition-funding' variant='body2'>
							{program.fundingLevel === -1 ? (
								<>
									<Typography variant='title3'>{employerName}</Typography> pays{' '}
									<Typography variant='title3'>100%</Typography> of your tuition per year
								</>
							) : (
								<>
									<Typography variant='title3'>{employerName}</Typography> pays up to{' '}
									<Typography variant='title3'>{funding}</Typography> per year
								</>
							)}
						</Typography>
						<Typography testid='tuition-funding-subtext' variant='body1'>
							{costMapping.funding.subtext}
						</Typography>

						<Typography className={styles.costItem} testid='tuition-cost' variant='body2'>
							{programCostUnavailable ? (
								"Remaining costs aren't available."
							) : (
								<>
									Estimated{' '}
									{remainingCosts === 0 ? (
										<>
											<Typography variant='title3'>remaining cost</Typography> is{' '}
										</>
									) : (
										<>
											<Typography variant='title3'>remaining costs</Typography> are{' '}
										</>
									)}
									<Typography variant='title3'>{cost}</Typography> for tuition
								</>
							)}
						</Typography>
						<Typography variant='body1'>
							{costMapping.cost.subtext}
							<CostPopover eventText={costMapping.cost.subtext} id='cost' location='section' />
						</Typography>
					</>
				</ShowMore>
			</Skeleton>
		</section>
	);
};
